import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { RARITY_LABELS_TR, RARITY_UI, revealRarityKey } from '../utils/collectionUi';
import { pickActivityThumbnail, feedAccentClasses, formatRelativeTimeTr } from '../utils/activityFeedUi';
import {
  qualifiesForLivePopup,
  formatActivityFeedMessage,
  defaultActivityFeedUiConfig,
} from '../utils/activityFeedMessages';
import FeedEventThumb from './FeedEventThumb';
import { getApiBase } from '../utils/apiBase';
import { getActivityFeedHighlightsCached, getActivityFeedUiConfigCached } from '../utils/activityFeedCachedClient';
import { FeedEventIcon } from '../utils/iconMap';

const API_URL = getApiBase() + '/api';

const POLL_MS = 30_000;
const DISMISS_MS = 6500;
const SEEN_IDS_KEY = 'dekosta_live_feed_seen_ids';
const MAX_SEEN = 80;

function eventDedupeKey(ev) {
  if (ev?.event_id) return String(ev.event_id);
  return `${ev?.created_at}|${ev?.event_type}|${ev?.user_display_name}|${ev?.asset_name}`;
}

function readSeenSet() {
  try {
    const raw = localStorage.getItem(SEEN_IDS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function writeSeenSet(set) {
  const arr = [...set];
  const trimmed = arr.slice(-MAX_SEEN);
  try {
    localStorage.setItem(SEEN_IDS_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore */
  }
}

/**
 * Highlights API üzerinden hafif polling; yalnızca seçilmiş olaylar için kısa süreli popup.
 */
export default function LiveFeedPopup() {
  const [active, setActive] = useState(null);
  const [uiConfig, setUiConfig] = useState(() => defaultActivityFeedUiConfig());
  // uiConfig state değişince fetch/polling callback'leri yeniden oluşturulmasın diye ref ile referans tutuyoruz.
  const uiConfigRef = useRef(uiConfig);
  const queueRef = useRef([]);
  const showingRef = useRef(false);
  const primedRef = useRef(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const seenRef = useRef(readSeenSet());

  useEffect(() => {
    uiConfigRef.current = uiConfig;
  }, [uiConfig]);

  const showNext = useCallback(() => {
    if (showingRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    showingRef.current = true;
    setActive(next);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setActive(null);
      showingRef.current = false;
      timerRef.current = null;
      showNext();
    }, DISMISS_MS);
  }, []);

  const processIncoming = useCallback(
    (events, cfgOverride) => {
      const cfg = cfgOverride || uiConfigRef.current;
      const list = Array.isArray(events) ? events : [];
      const templates = cfg.message_templates || {};

      if (!primedRef.current) {
        list.forEach((ev) => seenRef.current.add(eventDedupeKey(ev)));
        writeSeenSet(seenRef.current);
        primedRef.current = true;
        if (!cfg.live_popup_enabled) return;
        return;
      }

      if (!cfg.live_popup_enabled) return;

      const sorted = [...list].sort((a, b) => {
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return ta - tb;
      });

      for (const ev of sorted) {
        const key = eventDedupeKey(ev);
        if (seenRef.current.has(key)) continue;
        if (!qualifiesForLivePopup(ev, cfg)) {
          seenRef.current.add(key);
          continue;
        }
        const msg = formatActivityFeedMessage(ev, templates);
        if (!msg || !String(msg).trim()) {
          seenRef.current.add(key);
          continue;
        }
        seenRef.current.add(key);
        queueRef.current.push({ ...ev, _popupMessage: msg });
      }
      writeSeenSet(seenRef.current);
      showNext();
    },
    [showNext]
  );

  const fetchHighlights = useCallback(async () => {
    try {
      const cfgData = await getActivityFeedUiConfigCached();
      const nextCfg =
        cfgData && typeof cfgData === 'object'
          ? { ...defaultActivityFeedUiConfig(), ...cfgData }
          : defaultActivityFeedUiConfig();
      setUiConfig(nextCfg);

      const highlightsData = await getActivityFeedHighlightsCached(30);
      processIncoming(highlightsData?.events, nextCfg);
    } catch {
      /* sessiz */
    }
  }, [processIncoming]);

  useEffect(() => {
    fetchHighlights();
    pollRef.current = window.setInterval(fetchHighlights, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } else {
        if (!pollRef.current) {
          fetchHighlights();
          pollRef.current = window.setInterval(fetchHighlights, POLL_MS);
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchHighlights]);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setActive(null);
    showingRef.current = false;
    showNext();
  }, [showNext]);

  const rk = active ? revealRarityKey(active.asset_rarity) : 'common';
  const ui = RARITY_UI[rk] || RARITY_UI.common;
  const popupGlow = uiConfig.enable_popup_glow !== false;
  const accent = active ? feedAccentClasses(active, { enableGlow: popupGlow }) : { frame: '', glow: '' };
  const showThumbnails = uiConfig.show_thumbnails !== false;
  const showRarityBadges = uiConfig.show_rarity_badges !== false;
  const showRelativeTime = uiConfig.show_relative_time !== false;
  const thumbUrl = active && showThumbnails ? pickActivityThumbnail(active) : null;
  const thumbSize =
    active?.event_type === 'rare_drop' && ['legendary', 'mythic'].includes(rk) ? 'lg' : 'md';

  return (
    <div
      className="pointer-events-none fixed z-[100] inset-x-0 top-0 flex justify-center sm:justify-end p-3 sm:p-4"
      aria-live="polite"
    >
      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key={eventDedupeKey(active)}
            role="status"
            initial={{ opacity: 0, y: -16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            className="pointer-events-auto w-full max-w-sm"
          >
            <div
              className={`relative overflow-hidden rounded-2xl bg-zinc-950/93 backdrop-blur-md px-3 py-3 pr-10 sm:px-4 border ${accent.frame} ${accent.glow}`}
            >
              <div className="flex gap-3 items-start">
                {showThumbnails ? (
                  <FeedEventThumb
                    url={thumbUrl}
                    size={thumbSize}
                    className="border-white/15 mt-0.5"
                  >
                    <div className={`flex h-full w-full items-center justify-center rounded-xl border border-white/10 bg-black/45 p-2 ${ui.chip}`}>
                      <FeedEventIcon eventType={active.event_type} size="medium" />
                    </div>
                  </FeedEventThumb>
                ) : (
                  <div className={`shrink-0 flex items-center justify-center rounded-xl border border-white/10 bg-black/45 p-2 mt-0.5 ${ui.chip}`}>
                    <FeedEventIcon eventType={active.event_type} size="medium" />
                  </div>
                )}
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium leading-snug text-white">{active._popupMessage}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {showRarityBadges && active.asset_rarity && active.event_type !== 'collection_score_milestone' && (
                      <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${ui.badge}`}>
                        {RARITY_LABELS_TR[rk]}
                      </span>
                    )}
                    {showRelativeTime && active.created_at && (
                      <span className="text-[10px] text-gray-500 tabular-nums">
                        {formatRelativeTimeTr(active.created_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="absolute right-2 top-2 rounded-lg p-1.5 text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Kapat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export { qualifiesForLivePopup } from '../utils/activityFeedMessages';
